import React, { useCallback, useEffect, useState } from 'react';
import { Link, Outlet, useLocation, useParams } from 'react-router-dom';
import { useAsync } from 'react-use';
import * as businessService from '../../data-layer/business/service';
import { BusinessStatus } from '../../mappers/business/toBusinessModel';
import { toBusinessUI } from '../../mappers/business/toBusinessUI';
import { BusinessCard, BusinessCardSkeleton } from '../../ui/business/BusinessCard';
import { MessengerButton } from '../../ui/business/MessengerWidget';
import { DashboardPage } from '../../ui/dashboard/DashboardPage';
import { WithHint } from '../../ui/feedback/Tooltip';
import { Box } from '../../ui/layout/Box';
import { Tab, Tabs } from '../../ui/navigation/Tabs';
import { Typography } from '../../ui/typography/Typography';
import { useEventSource } from '../../utils/LocationContext';
import {
    canEditBusiness,
    canSeeSupportChat,
    canViewBusinessAsAdmin,
    hasAccessToBusinessStat,
} from '../../utils/accessService';
import {
    analyticsService,
    BusinessTransitionType,
    EventSource,
} from '../../utils/analyticsService';
import {
    ymTrackAllPostClick,
    ymTrackChangeAvatar,
    ymTrackChangeCover,
    ymTrackChatClick,
    ymTrackPublishedClick,
    ymTrackSaveAvatar,
    ymTrackSaveAvatarError,
    ymTrackSaveCover,
    ymTrackSaveCoverError,
} from '../../utils/metrika';
import { useMeta } from '../../utils/useMeta';
import { DialogRoute, useOpenDialog } from '../ModalRoute';
import { DIALOGS } from '../constants';
import { useCurrentUser } from '../users/useCurrentUser';
import { useCanonicalUrl } from '../utils/useCanonicalUrl';
import { BusinessImagePersistence } from './BusinessImagePersistence';
import { useBusinessCallbacks } from './useBusinessCallbacks';
import { useBusinessModel } from './useBusinessModel';
import type { BusinessModel, BusinessStatsModel } from '../../data-layer/business/types';

const DEFAULT_STAT_PERIOD = '7';

const buildTabs = (businessId: string) => {
    if (businessId) {
        return [
            {
                value: `/businesses/${businessId}`,
                label: 'Опубликовано',
            },
            {
                value: `/businesses/${businessId}/moderation`,
                label: (
                    <WithHint
                        message={
                            'В этой вкладке находятся все посты: опубликованные/ на модерации/ отклоненные.\n\nЧерновики можно найти в разделе "Управление лентой"'
                        }
                    >
                        Все посты
                    </WithHint>
                ),
            },
        ];
    }

    return [];
};

const buildMeta = (model: BusinessModel | undefined, canonicalUrl: string) => {
    if (!model) {
        return {};
    }

    return {
        title: `${model.displayName} • Яндекс Ритм`,
        description: model.description,
        links: [{ rel: 'canonical', href: canonicalUrl }],
        openGraph: {
            title: `Яндекс Ритм | ${model.publicId}`,
            description: model.description,
            image: model.avatarUrl,
        },
    };
};

interface BusinessStatisticProps {
    stats: BusinessStatsModel;
}

export const BusinessDetails = () => {
    const params = useParams();
    const businessId = params.businessId!;
    const { pathname } = useLocation();
    const openDialog = useOpenDialog();
    const business = useBusinessModel({ businessId });
    const currentUser = useCurrentUser();
    const businessCallbacks = useBusinessCallbacks();
    const { eventSource, prevEventSource } = useEventSource();
    const [businessStatistic, setBusinessStatistic] = useState<BusinessStatisticProps | undefined>(
        undefined,
    );

    const { loading: statisticLoading } = useAsync(async () => {
        setBusinessStatistic(undefined);
        if (hasAccessToBusinessStat(businessId, currentUser)) {
            setBusinessStatistic(
                await businessService.loadBusinessStatistic(businessId, DEFAULT_STAT_PERIOD),
            );
        }
    }, [businessId, currentUser]);

    const onBusinessTransition = useCallback(
        (id: BusinessModel['id'], es: EventSource) => {
            analyticsService.trackBusinessTransition({
                businessId: id,
                eventSource: es,
                businessTransitionType: BusinessTransitionType.IN,
            });
        },
        [businessId],
    );

    useEffect(() => {
        if (business?.id && eventSource === EventSource.BUSINESS) {
            onBusinessTransition(business.id, prevEventSource || EventSource.BUSINESS);
        }

        return () => {
            if (business?.id && eventSource === EventSource.BUSINESS) {
                analyticsService.trackBusinessTransition({
                    businessId: business.id,
                    eventSource: EventSource.BUSINESS,
                    businessTransitionType: BusinessTransitionType.OUT,
                });
            }
        };
    }, [business?.id, eventSource, prevEventSource]);

    const canonicalUrl = useCanonicalUrl();
    useMeta(buildMeta(business, canonicalUrl));

    const tabs = buildTabs(businessId);

    const onReport = useCallback(() => {
        openDialog(DIALOGS.REPORT_DIALOG, { targetId: businessId, targetType: 'business' });
    }, [businessId]);

    const onBadgeClick = useCallback(() => {
        if (business?.status === BusinessStatus.WITHOUT_RANGE) {
            openDialog(DIALOGS.WITHOUT_RANGE, {
                editLink: business?.editLink,
            });
        }
    }, [business?.status, business?.editLink]);

    const onTabClick = useCallback(
        (value: string) => {
            if (value === `/businesses/${businessId}`) {
                ymTrackPublishedClick({
                    page: 'Профиль бизнеса',
                    userLogin: currentUser?.login,
                    businessId: currentUser?.profile?.publicId,
                });
            }
            if (value === `/businesses/${businessId}/moderation`) {
                ymTrackAllPostClick({
                    page: 'Профиль бизнеса',
                    userLogin: currentUser?.login,
                    businessId: currentUser?.profile?.publicId,
                });
            }
        },
        [currentUser?.login, currentUser?.profile?.publicId],
    );

    const onPersistBusinessCover = useCallback(() => {
        ymTrackChangeCover({
            page: 'Профиль бизнеса',
            userLogin: currentUser?.login,
            businessId: currentUser?.profile?.publicId,
        });
        openDialog(DIALOGS.PERSISTENCE_COVER);
    }, [currentUser?.login, currentUser?.profile?.publicId]);

    const onPersistBusinessAvatar = useCallback(() => {
        ymTrackChangeAvatar({
            page: 'Профиль бизнеса',
            userLogin: currentUser?.login,
            businessId: currentUser?.profile?.publicId,
        });
        openDialog(DIALOGS.PERSISTENCE_AVATAR);
    }, [currentUser?.login, currentUser?.profile?.publicId]);

    const trackSuccessAvatarChange = useCallback(() => {
        ymTrackSaveAvatar({
            page: 'Профиль бизнеса',
            userLogin: currentUser?.login,
            businessId: currentUser?.profile?.publicId,
        });
    }, [currentUser?.login, currentUser?.profile?.publicId]);

    const trackErrorAvatarChange = useCallback(() => {
        ymTrackSaveAvatarError({
            page: 'Профиль бизнеса',
            userLogin: currentUser?.login,
            businessId: currentUser?.profile?.publicId,
        });
    }, [currentUser?.login, currentUser?.profile?.publicId]);

    const trackSuccessCoverChange = useCallback(() => {
        ymTrackSaveCover({
            page: 'Профиль бизнеса',
            userLogin: currentUser?.login,
            businessId: currentUser?.profile?.publicId,
        });
    }, [currentUser?.login, currentUser?.profile?.publicId]);

    const trackErrorCoverChange = useCallback(() => {
        ymTrackSaveCoverError({
            page: 'Профиль бизнеса',
            userLogin: currentUser?.login,
            businessId: currentUser?.profile?.publicId,
        });
    }, [currentUser?.login, currentUser?.profile?.publicId]);

    const onChatClick = useCallback(() => {
        ymTrackChatClick({
            page: 'Профиль бизнеса',
            userLogin: currentUser?.login,
            businessId: currentUser?.profile?.publicId,
        });
    }, [currentUser?.login, currentUser?.profile?.publicId]);

    return (
        <DashboardPage position="relative" py={0}>
            {business ? (
                <BusinessCard
                    {...businessCallbacks}
                    business={toBusinessUI(business)}
                    onPersistBusinessCover={onPersistBusinessCover}
                    onPersistBusinessAvatar={onPersistBusinessAvatar}
                    user={currentUser}
                    businessStatistic={businessStatistic}
                    statisticLoading={statisticLoading}
                    onReport={onReport}
                    onBadgeClick={onBadgeClick}
                >
                    {canViewBusinessAsAdmin(business.id, currentUser) && (
                        <Tabs px={{ xs: 1, sm: 3 }} value={pathname} onChange={onTabClick}>
                            {tabs.map((tabItem) => (
                                <Tab
                                    key={tabItem.value}
                                    as={Link}
                                    width="50%"
                                    replace
                                    to={tabItem.value}
                                    value={tabItem.value}
                                    textDecoration="none"
                                >
                                    <Typography
                                        color="inherit"
                                        variant="body"
                                        weight="medium"
                                        textAlign="center"
                                    >
                                        {tabItem.label}
                                    </Typography>
                                </Tab>
                            ))}
                        </Tabs>
                    )}
                </BusinessCard>
            ) : (
                <BusinessCardSkeleton mb={{ xs: 0.5, sm: 1 }} />
            )}
            <Box width="100%">
                <Outlet key={businessId} />

                {business && canSeeSupportChat(business.id, currentUser) && (
                    <Box position="fixed" right={2} bottom={{ xs: 8, sm: 2 }}>
                        <MessengerButton onClick={onChatClick} />
                    </Box>
                )}
                {business && canEditBusiness(business.id, currentUser) && (
                    <>
                        <DialogRoute
                            name={DIALOGS.PERSISTENCE_AVATAR}
                            element={
                                <BusinessImagePersistence
                                    business={business}
                                    cropShape="round"
                                    property="avatarUrl"
                                    title={
                                        <WithHint message="Выбирайте качественное изображение размером не менее 150x150 px">
                                            Выберите фото профиля
                                        </WithHint>
                                    }
                                    trackSuccessChange={trackSuccessAvatarChange}
                                    trackError={trackErrorAvatarChange}
                                    size={{ width: 150, height: 150 }}
                                />
                            }
                        />

                        <DialogRoute
                            name={DIALOGS.PERSISTENCE_COVER}
                            element={
                                <BusinessImagePersistence
                                    business={business}
                                    property="coverUrl"
                                    title={
                                        <WithHint message="Выбирайте качественное изображение размером не менее 1920x1080 px">
                                            Выберите обложку профиля
                                        </WithHint>
                                    }
                                    size={{ width: 1920, height: 1080 }}
                                    trackSuccessChange={trackSuccessCoverChange}
                                    trackError={trackErrorCoverChange}
                                />
                            }
                        />
                    </>
                )}
            </Box>
        </DashboardPage>
    );
};
